<script setup lang="ts">
import type { IPoint } from '@/interfaces/geometry';
import { getPointerPoint } from '@/utils/dom.utils';
import { useCurrentTenant } from '@/composables/schoolAdmin/useCurrentTenant';
import { hasOverallAssessmentRating } from 'common/utils/performance.assessment.entry.utils';
import { useClassStudents } from '@/composables/student/useClassStudents';
import type { Components } from '@/api/generated/api';
import { UserFeedbackEntity } from '@/api/generated/api.enum';
import { useCompetenceAssessment } from '@/composables/competenceAssessment/useCompetenceAssessment';
import { useGradeCalculationSettings } from '@/composables/gradeCalculation/settings/useGradeCalculationSettings';
import { usePerformanceAssessmentEntryDetail } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentEntryDetail';
import { usePerformanceAssessmentEntryMutations } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentEntryMutations';
import { usePerformanceAssessmentEntryDuplicateGradingsConfirm } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentEntryDuplicateGradingsConfirm';
import { usePerformanceAssessmentCollectiveScope } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentCollectiveScope';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useCompetenceDomain } from '@/composables/useCompetenceDomain';
import { useConfirmDialog } from '@/composables/useConfirmDialog';
import { useMobile } from '@/composables/useMobile';
import { useDisplayName } from '@/composables/useDisplayName';
import { usePerformanceAssessmentStudentEntryMutations } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentStudentEntryMutations';
import { useStudentSelectionLabel } from '@/composables/useStudentSelectionLabel';
import { useUserRatingDialog } from '@/composables/userFeedback/useUserRatingDialog';
import { usePerformanceAssessmentEntryPdf } from '@/composables/performanceAssessmentEntry/usePerformanceAssessmentEntryPdf';
import OpenBadge from '@/core/badges/OpenBadge.vue';
import MobileSideDrawer from '@/core/dialogs/MobileSideDrawer.vue';
import TabbedDetailDialog from '@/core/dialogs/tabbed-detail-dialog/TabbedDetailDialog.vue';
import type {
  ITabbedDetailDialogTab,
  TTabbedDetailDialogCloseReason,
} from '@/core/dialogs/tabbed-detail-dialog/tabbedDetailDialog.types';
import { showAnchoredPicker } from '@/core/dialogs/anchoredPicker.utils';
import { createPerformanceAssessmentActionMenu } from '@/core/dialogs/entryActionMenu.utils';
import ThreeDotsActionDialog from '@/core/dialogs/ThreeDotsActionDialog.vue';
import InlineDatePicker from '@/core/elements/InlineDatePicker.vue';
import LocalLoadingSpinner from '@/core/elements/LocalLoadingSpinner.vue';
import SchoolSubjectBadge from '@/core/elements/SchoolSubjectBadge.vue';
import GradeCalculationEntryTagSelect from '@/core/gradeCalculation/GradeCalculationEntryTagSelect.vue';
import MobileDetailNavigationBar from '@/core/navigation/MobileDetailNavigationBar.vue';
import CompetenceSelectionPopover from '@/core/selects/CompetenceSelectionPopover.vue';
import StudentSelectionPopover from '@/core/selects/StudentSelectionPopover.vue';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { useTrialUpsellStore } from '@/stores/useTrialUpsellStore';
import { hasMissingEligibleStudents } from '@/utils/students.utils';
import { computed, nextTick, ref, toRefs, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { ICollectiveAssessmentRatingData } from './performanceAssessmentCollectiveAssessment.types';
import type { IPerformanceAssessmentEntryDetailDialogShowParams } from './performanceAssessmentEntryDetailDialog.types';
import PerformanceAssessmentEntryCollectiveAssessmentControl from './PerformanceAssessmentEntryCollectiveAssessmentControl.vue';
import PerformanceAssessmentEntryDetailsTab from './PerformanceAssessmentEntryDetailsTab.vue';
import PerformanceAssessmentStudentList from './PerformanceAssessmentStudentList.vue';
import PerformanceAssessmentStudentEntryEditor from '@/core/performanceAssessments/student-entry/PerformanceAssessmentStudentEntryEditor.vue';
import { usePerformanceAssessmentEntryDetailViewModel } from './usePerformanceAssessmentEntryDetailViewModel';
import { usePerformanceAssessmentEntryRelations } from './usePerformanceAssessmentEntryRelations';
import { usePerformanceAssessmentEntryStudentSelection } from './usePerformanceAssessmentEntryStudentSelection';
import { usePerformanceAssessmentCollectiveAssessment } from './usePerformanceAssessmentCollectiveAssessment';

const emit = defineEmits<{
  'delete:performanceAssessmentEntry': [id: number];
  'update:performanceAssessmentEntry': [id: number];
}>();

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    highlightUnevaluated?: boolean;
    onClose?: () => Promise<void> | void;
  }>(),
  {
    disabled: false,
    highlightUnevaluated: true,
    onClose: undefined,
  },
);
const { disabled } = toRefs(props);

const { t } = useI18n();
const { confirm } = useConfirmDialog();
const { isMobile } = useMobile();
const { getStudentDisplayName } = useDisplayName();
const { allSchoolSubjects } = useSchoolSubject();
const { allCompetenceDomains } = useCompetenceDomain();
const { currentCompetenceAssessment } = useCompetenceAssessment();
const { currentClass } = useCurrentClass();
const { allStudents } = useClassStudents(() => currentClass.value?.id);
const { bulkUpdatePerformanceAssessmentStudentEntries } = usePerformanceAssessmentStudentEntryMutations();
const { currentTenant } = useCurrentTenant();
const { gradeConfiguration, gradeTags, isGradeCalculationEnabledForSchoolLevel } = useGradeCalculationSettings(
  () => currentTenant.value?.id,
);
const { deletePerformanceAssessmentEntry, duplicatePerformanceAssessmentEntry, updatePerformanceAssessmentEntry } =
  usePerformanceAssessmentEntryMutations();
const { getPerformanceAssessmentEntryById } = usePerformanceAssessmentEntryDetail();
const { confirmIncludeGradings } = usePerformanceAssessmentEntryDuplicateGradingsConfirm();
const { checkAndShowRatingDialog } = useUserRatingDialog();
const trialUpsellStore = useTrialUpsellStore();
const { startPdfOperation, loading: pdfLoading } = usePerformanceAssessmentEntryPdf();

const dialogRef = useTemplateRef<InstanceType<typeof TabbedDetailDialog>>('dialogRef');
const datePickerRef = useTemplateRef<InstanceType<typeof InlineDatePicker>>('datePickerRef');
const detailsTabRef = useTemplateRef<InstanceType<typeof PerformanceAssessmentEntryDetailsTab>>('detailsTabRef');
const studentSelectionPopoverRef =
  useTemplateRef<InstanceType<typeof StudentSelectionPopover>>('studentSelectionPopoverRef');
const competenceSelectionPopoverRef = useTemplateRef<InstanceType<typeof CompetenceSelectionPopover>>(
  'competenceSelectionPopoverRef',
);
const mobileStudentNavigationRef =
  useTemplateRef<InstanceType<typeof MobileDetailNavigationBar>>('mobileStudentNavigationRef');
const entry = ref<Components.Schemas.PerformanceAssessmentEntryDto | null>(null);
const studentContentRef = useTemplateRef<HTMLElement>('studentContentRef');
const pinnedStudentId = ref<number | undefined>();
const activeTab = ref('documentation');
const description = ref('');
const isUpdatingDate = ref(false);
const isApplyingDescriptionToAll = ref(false);
const suppressPostCloseActions = ref(false);
const mobileStudentSidebarOpen = ref(false);
const swipeStart = ref<IPoint | null>(null);
const relationPickerAnchor = ref<HTMLElement | null>(null);

const {
  allAvailableCompetenceDomains,
  eligibleStudents,
  openStudentCount,
  performanceAssessment,
  schoolSubject,
  selectableStudents: studentsForSelection,
  selectedCompetences,
  studentViews,
} = usePerformanceAssessmentEntryDetailViewModel({
  allCompetenceDomains,
  allStudents,
  entry,
});

const tabs = computed<ITabbedDetailDialogTab[]>(() => [
  { label: t('performanceAssessmentEntry.documentation'), value: 'documentation' },
  { label: t('general.details'), value: 'details' },
]);

const dialogTitle = computed(() => entry.value?.label ?? '');
const dialogDate = computed(() => entry.value?.date ?? '');

const {
  initializeSelection,
  isOverview,
  selectedStudentId,
  selectedStudentView,
  selectStudent: updateStudentSelection,
  selectRelativeStudent: moveStudentSelection,
  visibleStudentViews,
} = usePerformanceAssessmentEntryStudentSelection(studentViews);

const { label: studentOverviewLabel } = useStudentSelectionLabel(
  () => studentViews.value.map(({ student }) => student),
  allStudents,
);
const mobileStudentLabel = computed(() =>
  selectedStudentView.value ? getStudentDisplayName(selectedStudentView.value.student) : studentOverviewLabel.value,
);

watch(
  selectedStudentId,
  () => {
    studentContentRef.value?.scrollTo({ top: 0 });
  },
  { flush: 'post' },
);
const showOpenStatus = computed(
  () => activeTab.value === 'documentation' && studentViews.value.length > 1 && openStudentCount.value > 0,
);

const collectiveAssessmentRatingData = computed<ICollectiveAssessmentRatingData | null>(() => {
  const persistedEntry = entry.value;
  if (!persistedEntry) return null;
  return {
    competenceRatings: persistedEntry.competenceObservations,
    overallRatings: persistedEntry.studentEntries,
    performanceAssessment: persistedEntry.performanceAssessment,
    schoolSubjectColor: persistedEntry.schoolSubject.color,
  };
});

const showCollectiveAssessmentControl = computed(() => {
  const ratingData = collectiveAssessmentRatingData.value;
  if (!ratingData || studentViews.value.length < 2) return false;
  return hasOverallAssessmentRating(ratingData.performanceAssessment) || ratingData.competenceRatings.length > 0;
});

const blurStudentAnnotation = (): void => {
  const focusedElement = document.activeElement;
  if (focusedElement instanceof HTMLElement && studentContentRef.value?.contains(focusedElement)) focusedElement.blur();
};

const selectStudent = (studentId: number | null): void => {
  blurStudentAnnotation();
  updateStudentSelection(studentId);
};

const selectRelativeStudent = (offset: -1 | 1): void => {
  blurStudentAnnotation();
  moveStudentSelection(offset);
};

const openMobileStudentSidebar = (): void => {
  datePickerRef.value?.hide();
  mobileStudentSidebarOpen.value = true;
};

const closeMobileStudentSidebar = (restoreFocus = true): void => {
  mobileStudentSidebarOpen.value = false;
  if (restoreFocus) void nextTick(() => mobileStudentNavigationRef.value?.focusOpenButton());
};

const handleMobileStudentSelect = (studentId: number | null): void => {
  selectStudent(studentId);
  closeMobileStudentSidebar();
};

const handleStudentSwipeStart = (event: TouchEvent): void => {
  if (event.touches.length !== 1) {
    swipeStart.value = null;
    return;
  }
  swipeStart.value = getPointerPoint(event.touches[0]);
};

const handleStudentSwipeEnd = (event: TouchEvent): void => {
  const start = swipeStart.value;
  swipeStart.value = null;
  if (!start || event.changedTouches.length !== 1 || studentViews.value.length < 2) return;
  const end = getPointerPoint(event.changedTouches[0]);
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  if (Math.abs(deltaX) < 60 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
  selectRelativeStudent(deltaX < 0 ? 1 : -1);
};

watch(activeTab, (tab) => {
  if (tab !== 'documentation') closeMobileStudentSidebar(false);
});

const relationCompetenceDomains = computed(() =>
  schoolSubject.value
    ? allAvailableCompetenceDomains.value.filter((domain) => domain.schoolSubjectId === schoolSubject.value?.id)
    : allAvailableCompetenceDomains.value,
);

const isGradeModuleActive = computed(() =>
  isGradeCalculationEnabledForSchoolLevel(currentClass.value?.schoolLevel ?? null),
);
const showGradeCalculationTagSelect = computed(() => isGradeModuleActive.value && gradeTags.value.length > 0);
const selectedGradeCalculationTag = computed(() => {
  const tagId = entry.value?.gradeCalculationTagId ?? null;
  return gradeTags.value.find((tag) => tag.id === tagId) ?? null;
});
const metadata = computed(() =>
  entry.value && activeTab.value === 'details'
    ? {
        creator: entry.value.creator,
        createdAt: entry.value.createdAt,
        updater: entry.value.lastEditor ?? entry.value.creator,
        updatedAt: entry.value.updatedAt,
      }
    : null,
);

const refreshEntry = async (synchronizedEntry?: Components.Schemas.PerformanceAssessmentEntryDto): Promise<void> => {
  if (!entry.value) return;
  const entryId = entry.value.id;
  entry.value = synchronizedEntry ?? (await getPerformanceAssessmentEntryById(entryId));
  emit('update:performanceAssessmentEntry', entryId);
  description.value = entry.value.description ?? '';
};

const { applyCollectiveAssessment: handleCollectiveAssessment } = usePerformanceAssessmentCollectiveAssessment({
  disabled,
  entry,
  refreshEntry,
});
const { getDescriptionScope } = usePerformanceAssessmentCollectiveScope();

const {
  addCompetence: handleDetailsAddCompetence,
  allEligibleStudentsSelected: allEligibleStudentsSelectedForSelection,
  isSaving: isSavingRelations,
  removeCompetence: handleDetailsRemoveCompetence,
  reset: resetRelations,
  selectAllStudents: handleDetailsSelectAllStudents,
  selectedStudents: selectedStudentsForSelection,
  updateCompetences: handleDetailsCompetencesUpdate,
  updateStudents: handleDetailsStudentsUpdate,
} = usePerformanceAssessmentEntryRelations({
  disabled,
  entry,
  refreshEntry,
  selectableStudents: studentsForSelection,
  studentViews,
});

const canAddStudents = computed(() => {
  if (props.disabled) return false;
  return hasMissingEligibleStudents(
    studentViews.value.map(({ student }) => student),
    eligibleStudents.value,
  );
});
const showStudentSidebar = computed(() => studentViews.value.length > 1 || canAddStudents.value);
const dialogSize = computed(() => (activeTab.value === 'details' || !showStudentSidebar.value ? 'compact' : 'wide'));

const rememberActionMenuAnchor = (anchor: HTMLElement): void => {
  relationPickerAnchor.value = anchor;
};

const openStudentSelection = async (anchor?: HTMLElement): Promise<void> => {
  if (props.disabled || isSavingRelations.value) return;
  await showAnchoredPicker({
    anchor,
    setAnchor: (value) => {
      relationPickerAnchor.value = value;
    },
    hideOther: () => competenceSelectionPopoverRef.value?.hide(),
    beforeShow: () => {
      if (mobileStudentSidebarOpen.value) closeMobileStudentSidebar(false);
    },
    show: () => studentSelectionPopoverRef.value?.show(),
  });
};

const openCompetenceSelection = async (): Promise<void> => {
  if (props.disabled || isSavingRelations.value || !entry.value?.studentEntries.length) {
    return;
  }
  await showAnchoredPicker({
    hideOther: () => studentSelectionPopoverRef.value?.hide(),
    show: () => competenceSelectionPopoverRef.value?.show(),
  });
};

const hideRelationPickers = (): void => {
  studentSelectionPopoverRef.value?.hide();
  competenceSelectionPopoverRef.value?.hide();
  relationPickerAnchor.value = null;
};

const applyDescriptionToAll = async (value: string): Promise<void> => {
  const selectedStudent = selectedStudentView.value?.student;
  if (!selectedStudent || props.disabled || isApplyingDescriptionToAll.value || !value.trim()) return;

  isApplyingDescriptionToAll.value = true;
  try {
    const persistedEntry = entry.value;
    if (!persistedEntry) return;
    const candidates = persistedEntry.studentEntries.map((studentEntry) => ({
      id: studentEntry.id,
      description: studentEntry.studentId === selectedStudent.id ? value : studentEntry.description,
    }));
    const scope = await getDescriptionScope(candidates, value);
    if (scope === 'none') return;

    const selectedStudentEntryId = persistedEntry.studentEntries.find(
      (studentEntry) => studentEntry.studentId === selectedStudent.id,
    )?.id;
    const targetIds = candidates
      .filter(
        (candidate) => candidate.id === selectedStudentEntryId || scope === 'all' || !candidate.description?.trim(),
      )
      .map((candidate) => candidate.id);
    await bulkUpdatePerformanceAssessmentStudentEntries({
      schoolClassId: persistedEntry.schoolClassId,
      performanceAssessmentEntryId: persistedEntry.id,
      studentEntryIds: targetIds,
      dto: { description: value },
    });
    await refreshEntry();
  } finally {
    isApplyingDescriptionToAll.value = false;
  }
};

const handleMaterialsUpdate = async (externalMaterialIds: number[]): Promise<void> => {
  if (!entry.value || props.disabled || isSavingRelations.value) return;
  isSavingRelations.value = true;
  try {
    await updatePerformanceAssessmentEntry({
      schoolClassId: entry.value.schoolClassId,
      entryId: entry.value.id,
      dto: { externalMaterialIds },
    });
    await refreshEntry();
  } finally {
    isSavingRelations.value = false;
  }
};

const handleDescriptionChange = async (value: string): Promise<void> => {
  description.value = value;
  if (!entry.value || props.disabled || value === (entry.value.description ?? '')) return;
  await updatePerformanceAssessmentEntry({
    schoolClassId: entry.value.schoolClassId,
    entryId: entry.value.id,
    dto: { description: value },
  });
  await refreshEntry();
};

const handleDetailsDescriptionInput = (value: string): void => {
  description.value = value;
};

const handleGradeCalculationTagChange = async (gradeCalculationTagId: number | null): Promise<void> => {
  if (!entry.value || props.disabled || entry.value.gradeCalculationTagId === gradeCalculationTagId) return;
  await updatePerformanceAssessmentEntry({
    schoolClassId: entry.value.schoolClassId,
    entryId: entry.value.id,
    dto: { gradeCalculationTagId },
  });
  await refreshEntry();
};

const handleDateChange = async (date: string): Promise<void> => {
  if (!entry.value || props.disabled || isUpdatingDate.value || entry.value.date === date) return;
  isUpdatingDate.value = true;
  try {
    await updatePerformanceAssessmentEntry({
      schoolClassId: entry.value.schoolClassId,
      entryId: entry.value.id,
      dto: { date },
    });
    await refreshEntry();
  } finally {
    isUpdatingDate.value = false;
  }
};

const duplicateEntry = async (): Promise<void> => {
  if (!entry.value || props.disabled) return;
  const source = entry.value;
  const pinnedStudentIdForDuplicate = pinnedStudentId.value;
  const studentIds = source.studentEntries.map((studentEntry) => studentEntry.studentId);
  const includeGradings = await confirmIncludeGradings(source, studentIds);
  if (includeGradings === null) return;
  const duplicated = await duplicatePerformanceAssessmentEntry({
    schoolClassId: source.schoolClassId,
    entryId: source.id,
    studentIds,
    includeGradings,
  });
  suppressPostCloseActions.value = true;
  await dialogRef.value?.hide();
  await show({ performanceAssessmentEntryId: duplicated.id, pinnedStudentId: pinnedStudentIdForDuplicate });
};

const deleteEntry = async (): Promise<void> => {
  if (!entry.value || props.disabled) return;
  const { id: entryId, schoolClassId } = entry.value;

  const confirmed = await confirm({
    title: t('performanceAssessmentEntry.detailDialog.confirmDelete.title'),
    description: t('performanceAssessmentEntry.detailDialog.confirmDelete.description'),
    confirmButtonLabel: t('general.delete'),
    declineButtonLabel: t('general.cancel'),
    declineOnClose: true,
    theme: 'danger',
  });
  if (!confirmed) return;

  await deletePerformanceAssessmentEntry({ schoolClassId, entryId });
  emit('delete:performanceAssessmentEntry', entryId);
  dialogRef.value?.hide();
};

const dropdownActions = computed(() =>
  createPerformanceAssessmentActionMenu({
    studentsLabel: t('students.editStudents'),
    competencesLabel: t('competence.editPlural'),
    editStudents: () => openStudentSelection(),
    editCompetences: openCompetenceSelection,
    duplicateLabel: t('general.duplicate'),
    duplicateEntry,
    printLabel: t('general.print'),
    printEntry: () => {
      if (entry.value) void startPdfOperation(entry.value.id);
    },
    printDisabled: !entry.value?.studentEntries.length,
    deleteLabel: t('general.delete'),
    deleteEntry,
  }),
);

const handleDialogClose = async (): Promise<void> => {
  const shouldRunPostCloseActions = entry.value !== null && !props.disabled;
  datePickerRef.value?.hide();
  detailsTabRef.value?.hideOverlays();
  hideRelationPickers();
  mobileStudentSidebarOpen.value = false;
  swipeStart.value = null;
  entry.value = null;
  initializeSelection();
  pinnedStudentId.value = undefined;
  activeTab.value = 'documentation';
  isUpdatingDate.value = false;
  isApplyingDescriptionToAll.value = false;
  relationPickerAnchor.value = null;
  resetRelations();
  if (suppressPostCloseActions.value) {
    suppressPostCloseActions.value = false;
    return;
  }
  await props.onClose?.();
  if (!shouldRunPostCloseActions) return;
  const performanceShown = await checkAndShowRatingDialog(UserFeedbackEntity.performanceAssessmentEntry);
  if (!performanceShown) await checkAndShowRatingDialog(UserFeedbackEntity.competenceEntry);
  trialUpsellStore.requestEligibilityCheck();
};

const beforeClose = async (reason: TTabbedDetailDialogCloseReason): Promise<boolean> => {
  if (reason === 'escape' && mobileStudentSidebarOpen.value) {
    closeMobileStudentSidebar();
    return false;
  }
  if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  await nextTick();
  return true;
};

const show = async (params: IPerformanceAssessmentEntryDetailDialogShowParams): Promise<void> => {
  datePickerRef.value?.hide();
  mobileStudentSidebarOpen.value = false;
  swipeStart.value = null;
  activeTab.value = 'documentation';
  isApplyingDescriptionToAll.value = false;
  entry.value = await getPerformanceAssessmentEntryById(params.performanceAssessmentEntryId);
  description.value = entry.value.description ?? '';
  pinnedStudentId.value = params.pinnedStudentId;
  initializeSelection(params.pinnedStudentId);
  await dialogRef.value?.show();
};

const hide = async (): Promise<void> => {
  await dialogRef.value?.hide();
};
defineExpose({ hide, show });
</script>

<template>
  <TabbedDetailDialog
    ref="dialogRef"
    v-model:active-tab="activeTab"
    :before-close="beforeClose"
    :close-disabled="pdfLoading"
    :metadata="metadata"
    :modal-overlay-active="mobileStudentSidebarOpen"
    :size="dialogSize"
    :tabs="tabs"
    :title="dialogTitle"
    @close="handleDialogClose"
  >
    <template #title-badge>
      <SchoolSubjectBadge v-if="schoolSubject" :school-subject="schoolSubject" />
    </template>

    <template #overlay>
      <MobileSideDrawer
        v-if="showStudentSidebar"
        :open="mobileStudentSidebarOpen"
        :title="studentOverviewLabel"
        @close="closeMobileStudentSidebar()"
      >
        <PerformanceAssessmentStudentList
          :grade-configuration="gradeConfiguration"
          :has-performance-visual="hasOverallAssessmentRating(performanceAssessment)"
          :overview-label="studentOverviewLabel"
          :selected-student-id="selectedStudentId"
          :show-add-students="canAddStudents"
          :student-views="studentViews"
          @request-add="openStudentSelection"
          @select="handleMobileStudentSelect"
        />
      </MobileSideDrawer>

      <StudentSelectionPopover
        ref="studentSelectionPopoverRef"
        :anchor-element="relationPickerAnchor"
        :all-eligible-students-selected="allEligibleStudentsSelectedForSelection"
        commit-on-close
        :disable-click-outside="isSavingRelations"
        :eligible-students="studentsForSelection"
        mobile-drawer
        :selected-students="selectedStudentsForSelection"
        @select:all-eligible-students="handleDetailsSelectAllStudents"
        @update:students="handleDetailsStudentsUpdate"
      />

      <CompetenceSelectionPopover
        ref="competenceSelectionPopoverRef"
        :anchor-element="relationPickerAnchor"
        :all-school-subjects="allSchoolSubjects"
        :available-competence-domains="relationCompetenceDomains"
        commit-on-close
        :disable-click-outside="isSavingRelations"
        :disabled="isSavingRelations"
        :hide-school-subjects="schoolSubject != null"
        mobile-drawer
        :selected-competences="selectedCompetences.map(({ competence }) => competence)"
        @add:competence="handleDetailsAddCompetence"
        @remove:competence="handleDetailsRemoveCompetence"
        @update:competences="handleDetailsCompetencesUpdate"
      />
    </template>

    <template #subtitle>
      <InlineDatePicker
        v-if="dialogDate"
        ref="datePickerRef"
        always-show-mobile-chevron
        :date="dialogDate"
        :disabled="disabled"
        :loading="isUpdatingDate"
        @update:date="handleDateChange"
      />
    </template>

    <template #header-status>
      <OpenBadge v-if="showOpenStatus" :open-count="openStudentCount" :total-count="studentViews.length" />
    </template>

    <template #header-actions>
      <GradeCalculationEntryTagSelect
        v-if="showGradeCalculationTagSelect"
        :current-tag="selectedGradeCalculationTag"
        :grade-tags="gradeTags"
        :read-only="disabled"
        variant="chip"
        @select:tag="handleGradeCalculationTagChange"
      />
      <ThreeDotsActionDialog
        v-if="entry && !disabled"
        :actions="dropdownActions"
        :min-width="280"
        @show="rememberActionMenuAnchor"
      />
    </template>

    <template #mobile-header-actions>
      <ThreeDotsActionDialog
        v-if="entry && !disabled"
        :actions="dropdownActions"
        :min-width="280"
        @show="rememberActionMenuAnchor"
      />
    </template>

    <template #mobile-toolbar>
      <MobileDetailNavigationBar
        v-if="activeTab === 'documentation' && showStudentSidebar"
        ref="mobileStudentNavigationRef"
        :label="mobileStudentLabel"
        :next-label="$t('performanceAssessmentEntry.detailDialog.nextStudent')"
        :open-label="$t('performanceAssessmentEntry.detailDialog.openStudentList')"
        :previous-label="$t('performanceAssessmentEntry.detailDialog.previousStudent')"
        @open="openMobileStudentSidebar"
        @previous="selectRelativeStudent(-1)"
        @next="selectRelativeStudent(1)"
      />
    </template>

    <template #default>
      <LocalLoadingSpinner :show="pdfLoading" />

      <div
        v-if="activeTab === 'documentation'"
        class="grid h-full min-h-0 gap-content overflow-hidden p-content max-md:flex max-md:flex-col max-md:gap-lg max-md:p-lg"
        :class="showStudentSidebar ? 'md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]' : 'md:grid-cols-[minmax(0,1fr)]'"
        @touchstart.passive="handleStudentSwipeStart"
        @touchend.passive="handleStudentSwipeEnd"
      >
        <aside v-if="showStudentSidebar" class="min-h-0 min-w-0 overflow-y-auto overscroll-contain max-md:hidden">
          <PerformanceAssessmentStudentList
            :grade-configuration="gradeConfiguration"
            :has-performance-visual="hasOverallAssessmentRating(performanceAssessment)"
            :overview-label="studentOverviewLabel"
            :selected-student-id="selectedStudentId"
            :show-add-students="canAddStudents"
            :student-views="studentViews"
            @request-add="openStudentSelection"
            @select="selectStudent"
          />
        </aside>

        <section class="flex min-h-0 min-w-0 flex-col overflow-hidden max-md:flex-1">
          <div
            v-if="visibleStudentViews.length"
            ref="studentContentRef"
            class="min-h-0 flex-1 overflow-y-auto overscroll-contain"
          >
            <ul v-if="entry" class="flex list-none flex-col gap-md">
              <PerformanceAssessmentStudentEntryEditor
                v-for="studentView in visibleStudentViews"
                :key="studentView.student.id"
                :grade-configuration="gradeConfiguration"
                :apply-description-to-all-loading="isApplyingDescriptionToAll"
                :available-competence-domains="allAvailableCompetenceDomains"
                :available-school-subjects="allSchoolSubjects"
                :disabled="disabled"
                :highlight-unrated="highlightUnevaluated && !studentView.fullyRated"
                :observations="studentView.observations"
                :performance-assessment-entry="entry"
                :selectable-student="isOverview"
                :show-apply-description-to-all="!isOverview && studentViews.length > 1"
                :show-attachments="!isOverview"
                :show-observation-editor="!isOverview"
                show-student-header
                :student="studentView.student"
                @apply:description-to-all="applyDescriptionToAll"
                @select:student="selectStudent"
                @update:performance-assessment-entry="refreshEntry"
              />
            </ul>
          </div>

          <footer
            v-if="showCollectiveAssessmentControl"
            class="flex shrink-0 justify-end pt-lg max-md:justify-center max-md:pb-[env(safe-area-inset-bottom)]"
          >
            <PerformanceAssessmentEntryCollectiveAssessmentControl
              v-if="collectiveAssessmentRatingData"
              :grade-configuration="gradeConfiguration"
              :available-competence-domains="allAvailableCompetenceDomains"
              :competence-assessment="currentCompetenceAssessment"
              :disabled="disabled"
              :rating-data="collectiveAssessmentRatingData"
              :apply="handleCollectiveAssessment"
            />
          </footer>
        </section>
      </div>

      <PerformanceAssessmentEntryDetailsTab
        v-else
        ref="detailsTabRef"
        :entry-id="entry!.id"
        :all-eligible-students-selected="allEligibleStudentsSelectedForSelection"
        :all-school-subjects="allSchoolSubjects"
        :competence-domains="allAvailableCompetenceDomains"
        :competence-selection-disabled="!entry?.studentEntries.length"
        :materials="entry?.materials ?? []"
        :description="description"
        :disabled="disabled"
        :eligible-students="studentsForSelection"
        :relations-saving="isSavingRelations"
        :school-subject-id="schoolSubject?.id ?? null"
        :selected-competences="selectedCompetences"
        :selected-students="selectedStudentsForSelection"
        :show-student-selection="studentViews.length > 0"
        @add:competence="handleDetailsAddCompetence"
        @update:materials="handleMaterialsUpdate"
        @commit:description="handleDescriptionChange"
        @remove:competence="handleDetailsRemoveCompetence"
        @select:all-students="handleDetailsSelectAllStudents"
        @update:competences="handleDetailsCompetencesUpdate"
        @update:description="handleDetailsDescriptionInput"
        @update:students="handleDetailsStudentsUpdate"
      />
    </template>

    <template v-if="isMobile && activeTab === 'details' && showGradeCalculationTagSelect" #footer-leading>
      <GradeCalculationEntryTagSelect
        class="mb-lg self-start"
        :current-tag="selectedGradeCalculationTag"
        :grade-tags="gradeTags"
        :read-only="disabled"
        variant="chip"
        @select:tag="handleGradeCalculationTagChange"
      />
    </template>
  </TabbedDetailDialog>
</template>
